// `/agents/:id/try` — "Try it": a quick, matter-less test session + chat
// preview of a saved agent (fn-71 / ADR 0035 / R12).
//
// Reached from the agent detail page (which is also the create/edit landing,
// so this covers the "after create" case). The page:
//   1. starts a MATTER-LESS admin session for the agent (fn-63 — `matterId`
//      omitted, never faked; see `try-session.ts`), waiting out the fn-67
//      bootstrap turn before the first message;
//   2. renders the ui-react `<Chat>` component for that session, inside the
//      same `PlatformProvider` + `.sbui-root` embed region the `/preview`
//      testbed uses.
//
// **Per-session/agent state reset (memory `per-session-ui-state-must-reset` /
// `per-session-reducer-reset-must-be`):**
//   - The page keys the inner tree by `agentId` (`<AgentTryGate key={agentId}>`)
//     so a different agent fully remounts — fresh start hook, fresh held
//     `sessionId`, fresh `<Chat>`.
//   - Each `<Chat key={sessionId}>` remounts on a session change ("New test
//     session"), and `<Chat>` additionally resets its own per-session state at
//     render time. Both are render-time/remount resets, never passive effects.
//
// **Admin-gated** (mirrors the Add/Edit form, R11): the Try affordance is part
// of the admin authoring loop, so the page hides itself from non-admins via
// `useIsPlatformAdmin` — defense-in-depth for a deep link (the detail button is
// already admin-only).

import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Chat, PlatformProvider } from '@sb-ai-platform/ui-react';

import { getConsolePlatformAdapter } from '../../platform-adapter.js';
import { trpc } from '../../trpc-client.js';
import { useTrySession } from './try-session.js';
import { useIsPlatformAdmin } from './use-is-platform-admin.js';

export function AgentTryPage(): React.ReactElement {
  const params = useParams<{ id: string }>();
  const agentId = params.id ?? '';
  if (agentId.length === 0) {
    return (
      <section className="page-agent-try" role="alert">
        <h1>Try agent</h1>
        <p>Missing agent id.</p>
      </section>
    );
  }
  // Key the whole tree by agentId so switching agents remounts everything —
  // the start hook, the held sessionId, and the chat — with no carry-over.
  return <AgentTryGate key={agentId} agentId={agentId} />;
}

function AgentTryGate({ agentId }: { readonly agentId: string }): React.ReactElement {
  const admin = useIsPlatformAdmin();
  if (!admin.isResolved) {
    return (
      <section className="page-agent-try" aria-busy="true">
        <h1>Try agent</h1>
        <p>Checking access…</p>
      </section>
    );
  }
  if (!admin.isAdmin) {
    return (
      <section className="page-agent-try" role="alert">
        <header className="agent-detail-header">
          <Link to="/agents" className="agent-detail-back">
            ← Agents
          </Link>
        </header>
        <h1>Try agent</h1>
        <p>You need platform-admin access to try agents.</p>
      </section>
    );
  }
  return <AgentTryInner agentId={agentId} />;
}

function AgentTryInner({ agentId }: { readonly agentId: string }): React.ReactElement {
  const agentQuery = trpc.agents.byId.useQuery({ agentId }, { enabled: agentId.length > 0 });
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const onStarted = useCallback((id: string): void => {
    setSessionId(id);
  }, []);
  const trySession = useTrySession(onStarted);

  const backToStart = useCallback((): void => {
    trySession.reset();
    setSessionId(null);
    // Per-session reset (memory per-session-ui-state-must-reset): the
    // uncontrolled kickoff draft must NOT carry into the next test session.
    setMessage('');
  }, [trySession]);

  if (agentQuery.isPending) {
    return (
      <section className="page-agent-try" aria-busy="true">
        <h1>Try agent</h1>
        <p>Loading agent…</p>
      </section>
    );
  }
  if (agentQuery.error !== null) {
    return (
      <section className="page-agent-try" role="alert">
        <header className="agent-detail-header">
          <Link to="/agents" className="agent-detail-back">
            ← Agents
          </Link>
        </header>
        <h1>Try agent</h1>
        <p>Failed to load agent: {agentQuery.error.message}</p>
      </section>
    );
  }

  const agent = agentQuery.data;

  // Once a session is started, hand off to the ui-react chat surface.
  if (sessionId !== null) {
    return (
      <section className="page-agent-try">
        <div className="preview-banner">
          <span className="preview-banner-label">
            Trying <strong>{agent.name}</strong> — quick test session (no matter attached)
          </span>
          <button type="button" className="btn btn--tertiary" onClick={backToStart}>
            New test session
          </button>
        </div>
        <div className="sbui-root preview-embed">
          <PlatformProvider adapter={getConsolePlatformAdapter()}>
            {/* key by sessionId so a new test session remounts Chat clean. */}
            <Chat key={sessionId} sessionId={sessionId} onBack={backToStart} />
          </PlatformProvider>
        </div>
      </section>
    );
  }

  // Start panel — type a first message, then start the matter-less session.
  const starting = trySession.status === 'starting';
  const canStart = message.trim().length > 0 && !starting;
  return (
    <section className="page-agent-try">
      <header className="agent-detail-header">
        <Link to={`/agents/${encodeURIComponent(agentId)}`} className="agent-detail-back">
          ← Agent
        </Link>
      </header>
      <h1>Try {agent.name}</h1>
      <form
        className="preview-matter-chat"
        onSubmit={(e) => {
          e.preventDefault();
          if (canStart) {
            trySession.start({ agentId, agentName: agent.name, message: message.trim() });
          }
        }}
      >
        <p className="preview-matter-chat-hint">
          Starts a quick, matter-less test session of this agent (no matter is attached) and opens a
          chat. Send a first message to begin.
        </p>
        <label className="preview-matter-chat-label">
          First message
          <textarea
            className="preview-matter-chat-textarea"
            rows={5}
            placeholder="Say hello, or ask the agent to do something…"
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
            disabled={starting}
          />
        </label>
        {trySession.error !== undefined && (
          <p className="preview-matter-chat-error" role="alert">
            {trySession.error}
          </p>
        )}
        <div className="preview-matter-chat-actions">
          <button type="submit" className="btn btn--primary" disabled={!canStart}>
            {starting ? 'Starting…' : 'Try it'}
          </button>
          <Link
            to={`/agents/${encodeURIComponent(agentId)}`}
            className="btn btn--tertiary"
            aria-disabled={starting}
            tabIndex={starting ? -1 : undefined}
            onClick={(event) => {
              // aria-disabled is semantics only — actually block navigation
              // while the start mutation is in flight.
              if (starting) event.preventDefault();
            }}
          >
            Cancel
          </Link>
        </div>
      </form>
    </section>
  );
}
